import { useEffect, useMemo, useState } from 'react';

import { DrawingShape } from '@common/models';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';

import { MessageWithAlerts } from './MessageWithAlerts';
import {
    AlertWithMetadata,
    ProductMatchingTargetParameters,
    SelectedItemWithFullRecord,
} from './interfaces/ProductMatchingSummary';
import {
    MatchingCheckSkipToken,
    matchingCheckSkipToken,
    useEquivalentProductMatching,
} from './useEquivalentProductMatching';

const isMatchingCheckSkipToken = (arg: unknown): arg is MatchingCheckSkipToken => {
    return arg === matchingCheckSkipToken;
};

const emptyArray = [] satisfies DrawingShape[] | SelectedItemWithFullRecord[];

type MessageWithAlertsProps = { children?: React.ReactNode };

const emptyObject: ProductMatchingTargetParameters = {};

/**
 * Triggers an equivalentProductMatchingCheck with the given args. If the argument is the skip token does nothing.
 */
export const useEquivalentProductMatchingSummaryAlerts = (
    _args: MatchingCheckSkipToken | ProductMatchingTargetParameters
): {
    /** Array with alerts */
    isCalculatingAlerts: boolean;
    /**
     * Component to be used as a modal
     */
    MessageWithAlerts: React.FC<MessageWithAlertsProps>;
    userMustCancel: boolean;
} => {
    const [alerts, setAlerts] = useState<AlertWithMetadata[]>(emptyArray);
    const [isCalculatingAlerts, setIsCalculatingAlerts] = useState<boolean>(false);
    const { getAlerts } = useEquivalentProductMatching();
    const { retailerSettings } = useRetailerSettings();

    const args: ProductMatchingTargetParameters = useMemo(() => {
        return isMatchingCheckSkipToken(_args) ? emptyObject : _args;
    }, [_args]);

    useEffect(() => {
        const _getAlerts = async (args: ProductMatchingTargetParameters) => {
            setIsCalculatingAlerts(true);
            const alerts = await getAlerts(args);
            setAlerts(alerts);
            setIsCalculatingAlerts(false);
        };

        if (args.zipCode || args.color || args.material || args.prodQteGrpID || args.storeId) {
            _getAlerts({
                zipCode: args.zipCode,
                color: args.color,
                material: args.material,
                prodQteGrpID: args.prodQteGrpID,
                storeId: args.storeId,
            });
        }
    }, [args.zipCode, args.color, args.material, args.prodQteGrpID, args.storeId, getAlerts]);

    const userMustCancel = alerts?.length > 0 && alerts.some((alert) => alert.userMustCancel);

    /**
     * Wrap a message and append the alerts message. If Setting for equivalent matching is not set to 2 just returns the message
     * @param param0
     * @returns Component to wrap message and return the message with alerts message
     */
    const MessageWithAlertsModalContent: React.FC<MessageWithAlertsProps> = ({ children }) => {
        if (userMustCancel) {
            return (
                <div>
                    {alerts.map((alert, index) => {
                        return <p key={index}>{alert.label}</p>;
                    })}
                    <div>{children}</div>
                </div>
            );
        }
        return (
            <MessageWithAlerts
                enableMatching={retailerSettings?.enableMatching}
                isCalculatingAlerts={isCalculatingAlerts}
                alerts={alerts}
            >
                {children}
            </MessageWithAlerts>
        );
    };

    return {
        isCalculatingAlerts,
        MessageWithAlerts: MessageWithAlertsModalContent,
        userMustCancel,
    };
};
